import { useEffect, useMemo, useState } from 'react';

import { type DocumentNode, type ErrorLike } from '@apollo/client';
import { useQuery } from '@apollo/client/react';

import { gql } from '@graphql/__generated__';
import {
  type NormalizedReview,
  type NormalizedTransaction,
  type QueryParams,
  getEntityType,
} from '@modules/_shared/components/visibility-drawer/queries';
import {
  GET_AGENT_REVIEWS_COUNT,
  GET_AGENT_TRANSACTIONS_COUNT,
  agentReviewsQuery,
  agentReviewsTransformer,
  agentTransactionsQuery,
  agentTransactionsTransformer,
} from '@modules/_shared/components/visibility-drawer/queries/agent-queries';
import {
  GET_AGENCY_REVIEWS_COUNT,
  GET_AGENCY_TRANSACTIONS_COUNT,
  agencyReviewsQuery,
  teamReviewsTransformer as agencyReviewsTransformer,
  teamTransactionsQuery as agencyTransactionsQuery,
  teamTransactionsTransformer as agencyTransactionsTransformer,
} from '@modules/_shared/components/visibility-drawer/queries/team-queries';
import type { VisibilityEntity } from '@modules/_shared/components/visibility-drawer/types';
import { getTwoYearsAgo } from '@modules/_shared/utils/dates';
import { getTeamReviewsWhere } from '@modules/_shared/utils/teamReviews';

const DUMMY_QUERY = gql(/* GraphQL */ `
  query DummyQuery {
    __typename
  }
`);

type EntityDataResult<T> = {
  data: T[];
  totalCount: number;
  averageRating?: number;
  loading: boolean;
  error: ErrorLike | null;
  setErrorLike: (error: ErrorLike | null) => void;
};

type UseEntityDataOptions = Omit<QueryParams, 'entityId'>;

type QueryConfig = {
  query: DocumentNode;
  getVariables: (params: QueryParams) => Record<string, any>;
} | null;

type DataTransformer<TData = Record<string, any>, TResult = any> = (
  data: TData,
  options?: { limit?: number; offset?: number },
) => {
  items: TResult[];
  totalCount: number;
  averageRating?: number;
};

const useEntityData = <T>(
  entity: VisibilityEntity,
  options: UseEntityDataOptions,
  queryConfig: QueryConfig,
  transformer: DataTransformer<any, T> | null,
): EntityDataResult<T> => {
  const [apolloError, setErrorLike] = useState<ErrorLike | null>(null);
  const hasQuery = !!queryConfig && !!transformer;

  const queryParams: QueryParams = {
    entityId: entity.id,
    teamId: options.teamId,
    limit: options.limit,
    offset: options.offset,
    skip: options.skip,
  };

  const { data, loading, error } = useQuery(queryConfig?.query ?? DUMMY_QUERY, {
    variables: queryConfig ? queryConfig.getVariables(queryParams) : {},
    skip: !hasQuery || options.skip,
  });

  useEffect(() => {
    if (error != null) {
      setErrorLike(error);
    }
  }, [error]);

  if (!hasQuery || loading || !data) {
    return {
      data: [],
      totalCount: 0,
      loading: hasQuery ? loading : false,
      error: apolloError,
      setErrorLike,
    };
  }

  const transformed = transformer(data, {
    limit: options.limit,
    offset: options.offset,
  });

  return {
    data: transformed.items,
    totalCount: transformed.totalCount,
    loading: false,
    error: apolloError,
    setErrorLike,
  };
};

export const useEntityReviews = (
  entity: VisibilityEntity,
  options: UseEntityDataOptions,
): EntityDataResult<NormalizedReview> => {
  const [apolloError, setErrorLike] = useState<ErrorLike | null>(null);
  const entityType = getEntityType(entity);
  const isAgent = entityType === 'users';

  const queryConfig =
    entityType === 'users' ? agentReviewsQuery : agencyReviewsQuery;

  const transformer =
    entityType === 'users' ? agentReviewsTransformer : agencyReviewsTransformer;

  const agentCountData = useQuery(GET_AGENT_REVIEWS_COUNT, {
    variables: { userId: entity.id },
    skip: !isAgent || options.skip,
  });

  const agencyCountData = useQuery(GET_AGENCY_REVIEWS_COUNT, {
    variables: { reviewsWhere: getTeamReviewsWhere(entity.id) },
    skip: isAgent || options.skip,
  });

  const dataResult = useEntityData(entity, options, queryConfig, transformer);
  const queryError = agentCountData.error ?? agencyCountData.error;

  useEffect(() => {
    if (queryError != null) {
      setErrorLike(queryError);
    }
  }, [queryError]);

  const agencyAggregate = agencyCountData.data?.reviews_aggregate?.aggregate;

  const totalCount = isAgent
    ? agentCountData.data?.users_by_pk?.reviews_aggregate?.aggregate?.count ?? 0
    : agencyAggregate?.count ?? 0;

  const averageRating = isAgent
    ? agentCountData.data?.users_by_pk?.reviews_aggregate?.aggregate?.avg
        ?.rating ?? 0
    : agencyAggregate?.avg?.rating ?? 0;

  return {
    ...dataResult,
    totalCount,
    averageRating,
    error: apolloError ?? dataResult.error,
    setErrorLike,
  };
};

export const useEntityTransactions = (
  entity: VisibilityEntity,
  options: UseEntityDataOptions,
): EntityDataResult<NormalizedTransaction> => {
  const [apolloError, setErrorLike] = useState<ErrorLike | null>(null);
  const entityType = getEntityType(entity);
  const isAgent = entityType === 'users';
  const twoYearsAgo = useMemo(
    () => getTwoYearsAgo().toISOString().slice(0, 10),
    [],
  );

  const queryConfig = isAgent
    ? agentTransactionsQuery
    : agencyTransactionsQuery;
  const transformer = isAgent
    ? agentTransactionsTransformer
    : agencyTransactionsTransformer;

  const agentCountData = useQuery(GET_AGENT_TRANSACTIONS_COUNT, {
    variables: { userId: entity.id, twoYearsAgo },
    skip: !isAgent || options.skip,
  });

  const agencyCountData = useQuery(GET_AGENCY_TRANSACTIONS_COUNT, {
    variables: { teamId: entity.id, twoYearsAgo },
    skip: isAgent || options.skip,
  });

  const dataResult = useEntityData(entity, options, queryConfig, transformer);
  const queryError = agentCountData.error ?? agencyCountData.error;

  useEffect(() => {
    if (queryError != null) {
      setErrorLike(queryError);
    }
  }, [queryError]);

  const totalCount = isAgent
    ? agentCountData.data?.property_transactions_aggregate?.aggregate?.count ??
      0
    : agencyCountData.data?.property_transactions_aggregate?.aggregate?.count ??
      0;

  return {
    ...dataResult,
    totalCount,
    error: apolloError ?? dataResult.error,
    setErrorLike,
  };
};
